import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon,
  Send,
  Check,
  Loader2,
  Phone,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { BRAND } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_WINDOWS = [
  { value: "morning", label: "Morning · 8a – 12p" },
  { value: "afternoon", label: "Afternoon · 12p – 5p" },
  { value: "evening", label: "Evening · 5p – 8p" },
];

const PROJECT_TYPES = [
  { value: "kitchen", label: "Kitchen Remodel" },
  { value: "bathroom", label: "Bathroom Remodel" },
  { value: "living-room", label: "Living Room / Flooring" },
  { value: "full-remodel", label: "Full-Home Renovation" },
  { value: "other", label: "Other / Not sure yet" },
];

const initialForm = {
  name: "",
  phone: "",
  email: "",
  preferred_date: null,
  time_window: "",
  project_type: "",
  notes: "",
};

const fieldBase =
  "w-full bg-white border border-stone-300 px-4 py-3 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 transition-colors text-sm";

const labelBase =
  "uppercase text-[11px] tracking-[0.2em] text-stone-500 font-semibold mb-2 block";

export const ScheduleForm = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit =
    form.name.trim().length >= 2 &&
    form.phone.trim().length >= 7 &&
    /.+@.+\..+/.test(form.email) &&
    form.preferred_date &&
    form.time_window &&
    form.project_type &&
    !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        preferred_date: format(form.preferred_date, "yyyy-MM-dd"),
        time_window: form.time_window,
        project_type: form.project_type,
        notes: form.notes.trim(),
      };
      await axios.post(`${API}/leads`, payload);
      setSuccess(true);
      toast.success("Appointment request received", {
        description: "We'll call you to confirm your scheduled time.",
      });
      setForm(initialForm);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please call us directly.";
      toast.error("Couldn't schedule", { description: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="schedule-form-success"
        className="bg-stone-900 text-white p-10 md:p-14"
      >
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#9B4819]/20 mb-6">
          <Check className="w-7 h-7 text-[#E8A976]" strokeWidth={2.25} />
        </div>
        <h3 className="font-display font-light text-3xl sm:text-4xl leading-tight">
          Thanks — request received.
        </h3>
        <p className="text-stone-300 mt-4 max-w-md leading-relaxed">
          We'll call you within one business day to confirm your appointment.
          In a hurry? Reach us directly at{" "}
          <a
            href={BRAND.phoneLink}
            className="text-[#E8A976] underline underline-offset-4 hover:text-white"
          >
            {BRAND.phoneDisplay}
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => setSuccess(false)}
          data-testid="schedule-form-reset"
          className="mt-8 text-xs tracking-[0.25em] uppercase text-stone-400 hover:text-white border-b border-stone-600 hover:border-white pb-1"
        >
          Schedule another
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="schedule-form"
      className="bg-white border border-stone-200 p-8 md:p-12"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={labelBase} htmlFor="sf-name">Full name</label>
          <input
            id="sf-name"
            data-testid="schedule-name"
            type="text"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className={fieldBase}
            placeholder="Jane Morgan"
            required
          />
        </div>
        <div>
          <label className={labelBase} htmlFor="sf-phone">Phone</label>
          <input
            id="sf-phone"
            data-testid="schedule-phone"
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            className={fieldBase}
            placeholder="503-000-0000"
            required
          />
        </div>
        <div className="md:col-span-2">
          <label className={labelBase} htmlFor="sf-email">Email</label>
          <input
            id="sf-email"
            data-testid="schedule-email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            className={fieldBase}
            placeholder="you@example.com"
            required
          />
        </div>

        <div>
          <label className={labelBase}>Preferred date</label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="schedule-date-trigger"
                className={`${fieldBase} text-left flex items-center justify-between`}
              >
                <span className={form.preferred_date ? "text-stone-900" : "text-stone-400"}>
                  {form.preferred_date
                    ? format(form.preferred_date, "EEEE, MMM d, yyyy")
                    : "Select a date"}
                </span>
                <CalendarIcon className="w-4 h-4 text-stone-500" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={form.preferred_date}
                onSelect={(d) => update("preferred_date", d)}
                disabled={(d) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return d < today;
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div>
          <label className={labelBase}>Time window</label>
          <Select
            value={form.time_window}
            onValueChange={(v) => update("time_window", v)}
          >
            <SelectTrigger
              data-testid="schedule-time-window"
              className={`${fieldBase} h-auto`}
            >
              <SelectValue placeholder="Pick a window" />
            </SelectTrigger>
            <SelectContent>
              {TIME_WINDOWS.map((w) => (
                <SelectItem
                  key={w.value}
                  value={w.value}
                  data-testid={`schedule-time-${w.value}`}
                >
                  {w.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-2">
          <label className={labelBase}>Project type</label>
          <Select
            value={form.project_type}
            onValueChange={(v) => update("project_type", v)}
          >
            <SelectTrigger
              data-testid="schedule-project-type"
              className={`${fieldBase} h-auto`}
            >
              <SelectValue placeholder="What are you remodeling?" />
            </SelectTrigger>
            <SelectContent>
              {PROJECT_TYPES.map((p) => (
                <SelectItem
                  key={p.value}
                  value={p.value}
                  data-testid={`schedule-project-${p.value}`}
                >
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-2">
          <label className={labelBase} htmlFor="sf-notes">
            Notes (optional)
          </label>
          <textarea
            id="sf-notes"
            data-testid="schedule-notes"
            rows={4}
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            className={`${fieldBase} resize-none`}
            placeholder="Tell us a bit about your project — square footage, must-haves, timing..."
          />
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-8 border-t border-stone-200">
        <p className="text-xs text-stone-500 max-w-sm">
          By scheduling, you agree to be contacted by Morgan Construction Group
          about your project. We don't share your info.
        </p>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          <a
            href={BRAND.phoneLink}
            data-testid="schedule-form-call-instead"
            className="inline-flex items-center justify-center gap-2 border border-stone-300 hover:border-stone-900 text-stone-900 px-6 py-3 text-sm font-medium tracking-wide transition-colors"
          >
            <Phone className="w-4 h-4" />
            Call instead
          </a>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="schedule-submit"
            className="inline-flex items-center justify-center gap-2 bg-[#9B4819] hover:bg-[#7A3813] disabled:bg-stone-300 disabled:cursor-not-allowed text-white px-7 py-3 text-sm font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Schedule appointment
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ScheduleForm;
